import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable, forkJoin, map, of } from 'rxjs'

import { NodeDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'
import { NodeApiService } from '@flowx/designer/shared/api/node.api'
import { TemplateConfigApiService } from '@flowx/designer/shared/api/template-config.api'
import { TemplateConfigInterface } from '@flowx/shared/interfaces'

import {
  AiDesignerContext,
  DesignerContext,
  DocumentContextReference,
  InitialMessageRequest,
  InitialMessageResponse,
  Message,
  SendFeedbackPayload,
} from '../../../common/ai.dictionary'
import { EditDiffPayload } from '../services/designer-new.service'
import { AiTemplateConfig, DesignerService } from '../services/designer.service'

export type GeneratePayload = {
  prompt: string
  prevAnswer?: string
  sessionId?: string
  contextImages?: File[]
  contextDocument?: { name: string; documentId: string }
  dataModel: string // string of Pick<DataModelEntity, 'name' | 'type' | 'description' | 'exampleValue'>
}

export type GenerateResponse = {
  answer: AiTemplateConfig
  session_id: string
  request_id: string
}

export type SaveDraftPayload = {
  navAreaId: NodeDefinition['navigationAreaId']
  draftConfig: TemplateConfigInterface
  mergedConfig: TemplateConfigInterface
  aiContext: AiDesignerContext
}

export type GenerateUiPayload = {
  question: string
  context?: AiTemplateConfig | DesignerContext | null
  session_id: string
  data_model: any
}

export type GenerateUiResponse = {
  question: string
  answer: AiTemplateConfig
  context: AiTemplateConfig | AiDesignerContext | null
  chat: string
  session_id: string
  request_id: string
  error_message: string
  warning_message: string
  location: {
    ci: number
    index: number
    prompt: string
  }
  image_type: {
    image_type: 'interface'
    summary: string
  }
  mapping: {
    mapped_fields: { an: string; key: string; model: string }[]
  }
}

export type GenerateUiFromImageResponse = GenerateUiResponse & {
  image_type: {
    image_type: string
    summary: string
  }
}

export type GenerateUiFromDocumentResponse = { error_message: string; response: string }

@Injectable({ providedIn: 'root' })
export class AiDesignerAPI extends ApiHttpService {
  #templateConfigApi = inject(TemplateConfigApiService)
  #nodeApi = inject(NodeApiService)
  #designer = `ai-engine/agents/designer`

  constructor() {
    super(inject<string>('BASE_URL' as any), inject(HttpClient))
  }

  initialMessage(payload: InitialMessageRequest): Observable<InitialMessageResponse> {
    return this.post(`${this.#designer}/initial`, payload)
  }

  generateUi(payload: GenerateUiPayload): Observable<GenerateUiResponse> {
    return this.post(`${this.#designer}/json`, payload)
  }

  editUi(payload: GenerateUiPayload): Observable<GenerateUiResponse> {
    return this.post(`${this.#designer}/edit`, payload)
  }

  extractUiFromDocument(
    documentUiReference: DocumentContextReference
  ): Observable<GenerateUiFromDocumentResponse> {
    const { documentId: collection_db, name: ui_key, session_id: session_id } = documentUiReference

    return this.post(`${this.#designer}/ui/extract`, { collection_db, ui_key, session_id })
  }

  extractUiFromDocumentNew(
    documentUiReference: DocumentContextReference
  ): Observable<GenerateUiFromDocumentResponse> {
    const {
      documentId: collection_db,
      name: ui_key,
      session_id: session_id,
      value: value,
      description: description,
    } = documentUiReference

    return this.post(`${this.#designer}/ui/extract`, {
      collection_db,
      ui_key,
      session_id,
      value,
      description,
    })
  }

  generateUiFromImage(formData: FormData): Observable<GenerateUiFromImageResponse> {
    return this.post(`${this.#designer}/image/translate`, formData)
  }

  getSSEActions(payload: InitialMessageRequest): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#designer}/actions`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  getSSEGenerateUI(formData: FormData): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#designer}/chat`, null, null, formData).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  getSSEGenerateUiFlow(formData: FormData): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#designer}/ui-flow/stream`, null, null, formData).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  // Phase 3: edit an existing UI flow via SSE
  getSSEEditUiFlow(formData: FormData): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#designer}/ui-flow/edit/stream`, null, null, formData).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  // Phase 4: edit a selected subtree of an existing UI flow via SSE
  getSSESubtreeEditUiFlow(formData: FormData): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(
      `${this.#designer}/ui-flow/subtree-edit/stream`,
      null,
      null,
      formData
    ).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  getSSEGenerateUiTemplate(formData: FormData): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#designer}/ui-template/stream`, null, null, formData).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  getSSEEditUiTemplate(formData: FormData): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#designer}/ui-template/edit/stream`, null, null, formData).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  // Save draft to DB
  saveDraft(payload: SaveDraftPayload): Observable<TemplateConfigInterface[]> {
    const { navAreaId, draftConfig, mergedConfig, aiContext } = payload
    try {
      const templateConfig = DesignerService.toBulkUpdatePayload({
        draftConfig: [draftConfig],
        mergedConfig,
        aiContext,
      })
      return this.#templateConfigApi
        .bulkUpdateTemplateConfigs(navAreaId, { added: templateConfig })
        .pipe(map((response) => response.added))
    } catch (e) {
      console.error('Error transforming template to AI', e)
      return of([])
    }
  }

  saveEditDiff(
    navAreaId: number,
    editDiffAppliedConfig: TemplateConfigInterface,
    diff: EditDiffPayload
  ): Observable<TemplateConfigInterface[]> {
    const operations: Observable<any>[] = []

    // 1. Handle edited + moved components: extract from applied config, update properties
    const modifiedIds = new Set<number>([
      ...diff.edited.map((e) => e.id),
      ...diff.moved.map((m) => m.id),
    ])
    if (modifiedIds.size > 0) {
      const modifiedNodes = this.#collectNodesById(editDiffAppliedConfig, modifiedIds)
      if (modifiedNodes.length > 0) {
        operations.push(this.#templateConfigApi.bulkUpdateTemplateConfigProperties(modifiedNodes))
      }
    }

    // 2. Handle added components: create each new node under its parent via createNodeTemplate
    if (diff.added.length > 0) {
      const draftNodesWithParent = this.#collectDraftNodesWithParent(
        editDiffAppliedConfig,
        modifiedIds
      )
      for (const { parentId, node } of draftNodesWithParent) {
        const cleanNode = this.#stripTempFields(node)
        cleanNode.uiTemplateParentId = parentId
        operations.push(
          this.#nodeApi
            .createNodeTemplate(navAreaId, cleanNode)
            .pipe(map((response) => response.resourceContent.template))
        )
      }
    }

    // 3. Handle removed components
    if (diff.removed.length > 0) {
      const removedConfigs = diff.removed.map(({ id }) => ({ id }) as TemplateConfigInterface)
      operations.push(this.#templateConfigApi.deleteTemplateConfigs(removedConfigs))
    }

    if (operations.length === 0) {
      return of([])
    }

    return forkJoin(operations).pipe(map((results) => results.flat().filter(Boolean)))
  }

  sendFeedback(payload: SendFeedbackPayload): Observable<string> {
    const { reason, feedbackType, message, requestId, sessionId } = payload

    return this.post(`${this.#designer}/feedback`, {
      feedback: {
        reason,
        response: feedbackType,
        user_message: message,
      },
      request_id: requestId,
      session_id: sessionId,
    })
  }

  #collectNodesById(config: TemplateConfigInterface, ids: Set<number>): TemplateConfigInterface[] {
    const result: TemplateConfigInterface[] = []
    const walk = (node: TemplateConfigInterface): void => {
      if (ids.has(node.id)) {
        result.push(node)
      }
      node.templateConfig?.forEach(walk)
    }
    walk(config)
    return result
  }

  #collectDraftNodesWithParent(
    config: TemplateConfigInterface,
    excludeIds: Set<number>
  ): { parentId: number; node: TemplateConfigInterface }[] {
    const result: { parentId: number; node: TemplateConfigInterface }[] = []
    const walk = (node: TemplateConfigInterface, parentId: number | null): void => {
      if ((node as any).draft === true && !excludeIds.has(node.id) && parentId !== null) {
        result.push({ parentId, node })
        return // don't walk children — they'll be included with their parent
      }
      node.templateConfig?.forEach((child) => walk(child, node.id))
    }
    walk(config, null)
    return result
  }

  #stripTempFields(node: TemplateConfigInterface): TemplateConfigInterface {
    const clean = { ...node } as any
    delete clean.id
    delete clean.draft
    delete clean.meta
    if (clean.templateConfig?.length) {
      clean.templateConfig = clean.templateConfig.map((child: TemplateConfigInterface) =>
        this.#stripTempFields(child)
      )
    }
    return clean
  }
}
